import { FormReviewData, MedicalHistory } from "@/types";

interface MedicalSummarySectionProps {
  formData: FormReviewData;
}

export function MedicalSummarySection({
  formData,
}: MedicalSummarySectionProps) {
  const medical: MedicalHistory =
    formData?.formData?.medical_history || ({} as MedicalHistory);
  const medications = formData?.formData?.medications || {};

  const renderArrayField = (
    label: string,
    value: any,
    showNone: boolean = true
  ) => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return showNone ? "None" : null;
    }
    if (Array.isArray(value)) {
      const filtered = value.filter((v) => v && v !== "None");
      return filtered.length > 0
        ? filtered.join(", ")
        : showNone
        ? "None"
        : null;
    }
    return value;
  };

  return (
    <div className="info-card medical-summary">
      <h3>Medical History</h3>
      <div
        className="medical-highlights"
        style={{ display: "flex", flexDirection: "column", gap: "16px" }}
      >
        {/* Medications Section - Brief Summary */}
        <MedicalInfoItem label="Current Medications:">
          {medications.current_medications || "No"}
          {medications.current_medications_list && (
            <div
              style={{
                marginTop: "4px",
                fontSize: "14px",
                color: "#6b7280",
              }}
            >
              {medications.current_medications_list}
            </div>
          )}
        </MedicalInfoItem>

        <MedicalInfoItem label="Medication Allergies:">
          {medications.medication_allergies_severe || "No"}
        </MedicalInfoItem>

        {/* Allergies */}
        {renderArrayField("Allergies", medical.allergies) && (
          <MedicalInfoItem label="Allergies:">
            {renderArrayField("", medical.allergies)}
            {medical.allergies_details && (
              <div
                style={{
                  marginTop: "4px",
                  fontSize: "14px",
                  color: "#6b7280",
                  whiteSpace: "pre-line",
                }}
              >
                {medical.allergies_details}
              </div>
            )}
          </MedicalInfoItem>
        )}

        {/* Heart & Circulation */}
        {renderArrayField("", medical.heart_circulation, false) && (
          <MedicalInfoItem label="Heart & Circulation:">
            {renderArrayField("", medical.heart_circulation)}
            {medical.heart_circulation_details && (
              <div
                style={{
                  marginTop: "4px",
                  fontSize: "14px",
                  color: "#6b7280",
                  whiteSpace: "pre-line",
                }}
              >
                {medical.heart_circulation_details}
              </div>
            )}
          </MedicalInfoItem>
        )}

        {/* Lungs */}
        {renderArrayField("", medical.lungs, false) && (
          <MedicalInfoItem label="Lungs:">
            {renderArrayField("", medical.lungs)}
            {medical.lungs_details && (
              <div
                style={{
                  marginTop: "4px",
                  fontSize: "14px",
                  color: "#6b7280",
                  whiteSpace: "pre-line",
                }}
              >
                {medical.lungs_details}
              </div>
            )}
          </MedicalInfoItem>
        )}

        {/* Gastrointestinal */}
        {renderArrayField("", medical.gastrointestinal, false) && (
          <MedicalInfoItem label="Gastrointestinal:">
            {renderArrayField("", medical.gastrointestinal)}
            {medical.gastrointestinal_details && (
              <div
                style={{
                  marginTop: "4px",
                  fontSize: "14px",
                  color: "#6b7280",
                  whiteSpace: "pre-line",
                }}
              >
                {medical.gastrointestinal_details}
              </div>
            )}
          </MedicalInfoItem>
        )}

        {/* Kidneys */}
        {renderArrayField("", medical.kidneys, false) && (
          <MedicalInfoItem label="Kidneys:">
            {renderArrayField("", medical.kidneys)}
            {medical.kidneys_details && (
              <div
                style={{
                  marginTop: "4px",
                  fontSize: "14px",
                  color: "#6b7280",
                  whiteSpace: "pre-line",
                }}
              >
                {medical.kidneys_details}
              </div>
            )}
          </MedicalInfoItem>
        )}

        {/* Endocrine */}
        {renderArrayField("", medical.endocrine, false) && (
          <MedicalInfoItem label="Endocrine:">
            {renderArrayField("", medical.endocrine)}
            {medical.endocrine_details && (
              <div
                style={{
                  marginTop: "4px",
                  fontSize: "14px",
                  color: "#6b7280",
                }}
              >
                {medical.endocrine_details}
              </div>
            )}
          </MedicalInfoItem>
        )}

        {/* Immune System */}
        {renderArrayField("", medical.immune_system, false) && (
          <MedicalInfoItem label="Immune System:">
            {renderArrayField("", medical.immune_system)}
            {medical.immune_system_details && (
              <div
                style={{
                  marginTop: "4px",
                  fontSize: "14px",
                  color: "#6b7280",
                  whiteSpace: "pre-line",
                }}
              >
                {medical.immune_system_details}
              </div>
            )}
          </MedicalInfoItem>
        )}

        {/* Cancer/Blood */}
        {renderArrayField("", medical.cancer_blood, false) && (
          <MedicalInfoItem label="Cancer/Blood:">
            {renderArrayField("", medical.cancer_blood)}
            {medical.cancer_blood_details && (
              <div
                style={{
                  marginTop: "4px",
                  fontSize: "14px",
                  color: "#6b7280",
                }}
              >
                {medical.cancer_blood_details}
              </div>
            )}
          </MedicalInfoItem>
        )}

        {/* Neurologic/Psychiatric */}
        {renderArrayField("", medical.neurologic_psychiatric, false) && (
          <MedicalInfoItem label="Neurologic/Psychiatric:">
            {renderArrayField("", medical.neurologic_psychiatric)}
            {medical.neurologic_psychiatric_details && (
              <div
                style={{
                  marginTop: "4px",
                  fontSize: "14px",
                  color: "#6b7280",
                  whiteSpace: "pre-line",
                }}
              >
                {medical.neurologic_psychiatric_details}
              </div>
            )}
          </MedicalInfoItem>
        )}

        {/* Musculoskeletal/Skin */}
        {renderArrayField("", medical.musculoskeletal_skin, false) && (
          <MedicalInfoItem label="Musculoskeletal/Skin:">
            {renderArrayField("", medical.musculoskeletal_skin)}
            {medical.musculoskeletal_skin_details && (
              <div
                style={{
                  marginTop: "4px",
                  fontSize: "14px",
                  color: "#6b7280",
                  whiteSpace: "pre-line",
                }}
              >
                {medical.musculoskeletal_skin_details}
              </div>
            )}
          </MedicalInfoItem>
        )}

        {/* Reproductive Health */}
        {renderArrayField("", medical.reproductive_health, false) && (
          <MedicalInfoItem label="Reproductive Health:">
            {renderArrayField("", medical.reproductive_health)}
            {medical.reproductive_health_details && (
              <div
                style={{
                  marginTop: "4px",
                  fontSize: "14px",
                  color: "#6b7280",
                  whiteSpace: "pre-line",
                }}
              >
                {medical.reproductive_health_details}
              </div>
            )}
          </MedicalInfoItem>
        )}

        {/* Surgeries */}
        {medical.surgeries && (
          <MedicalInfoItem label="Surgeries:">
            {medical.surgeries}
          </MedicalInfoItem>
        )}

        {/* Other Conditions */}
        {medical.other_conditions && (
          <MedicalInfoItem label="Other Conditions:">
            {medical.other_conditions}
          </MedicalInfoItem>
        )}

        {/* Routine Vaccinations */}
        <MedicalInfoItem label="Routine Vaccinations Up to Date:" isLast>
          {medical.routine_vaccinations || "Not specified"}
        </MedicalInfoItem>
      </div>
    </div>
  );
}

function MedicalInfoItem({
  label,
  children,
  isLast = false,
}: {
  label: string;
  children: React.ReactNode;
  isLast?: boolean;
}) {
  return (
    <div
      className={`medical-item grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-4 items-start ${isLast ? "" : "pb-3 border-b border-gray-200"}`}
    >
      <label style={{ fontWeight: "700", color: "#374151" }}>{label}</label>
      <span style={{ color: "#1f2937" }}>{children}</span>
    </div>
  );
}
